/**
 * FilePreviewModal - Modal xem file trong iframe
 * Hỗ trợ Google Docs, Sheets, Slides, PDF, Images
 *
 * Keyboard shortcuts:
 * - Escape: Đóng modal
 * - C: Copy link
 * - O: Mở file gốc
 * - D: Download
 * - ArrowLeft/ArrowUp: File trước
 * - ArrowRight/ArrowDown: File sau
 */
import { useState, useEffect, useCallback } from 'react'
import clsx from 'clsx'
import {
  HiOutlineX,
  HiOutlineExternalLink,
  HiOutlineDownload,
  HiOutlineLink,
  HiOutlineDocument,
  HiOutlinePhotograph,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from 'react-icons/hi'
import toast from 'react-hot-toast'
import driveService from '../../services/driveService'

function FilePreviewModal({
  file,
  isOpen,
  onClose,
  // Navigation props (optional)
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  currentIndex,
  totalFiles
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  // Handle copy link
  const handleCopyLink = useCallback(async () => {
    if (!file) return
    const success = await driveService.copyLink(file)
    if (success) {
      toast.success('Da copy link!')
    } else {
      toast.error('Khong the copy link')
    }
  }, [file])

  // Handle open original
  const handleOpenOriginal = useCallback(() => {
    if (file?.view_url) {
      window.open(file.view_url, '_blank')
    }
  }, [file])

  // Handle download
  const handleDownload = useCallback(() => {
    if (!file) return
    if (file.download_url) {
      window.open(file.download_url, '_blank')
    } else if (file.drive_id) {
      window.open(`https://drive.google.com/uc?export=download&id=${file.drive_id}`, '_blank')
    }
  }, [file])

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

      switch (e.key) {
        case 'Escape':
          e.preventDefault()
          onClose()
          break
        case 'c':
        case 'C':
          e.preventDefault()
          handleCopyLink()
          break
        case 'o':
        case 'O':
          e.preventDefault()
          handleOpenOriginal()
          break
        case 'd':
        case 'D':
          e.preventDefault()
          handleDownload()
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault()
          if (hasPrevious && onPrevious) onPrevious()
          break
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault()
          if (hasNext && onNext) onNext()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, handleCopyLink, handleOpenOriginal, handleDownload, hasPrevious, hasNext, onPrevious, onNext])

  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      setError(false)
      // Track access
      if (file?.drive_id) {
        driveService.trackAccess(file.drive_id)
      }
    }
  }, [isOpen, file])

  if (!isOpen || !file) return null

  // Get embed URL for different file types
  const getEmbedUrl = () => {
    const mime = file.mime_type || ''
    const driveId = file.drive_id

    // Google Docs types - use preview URL
    if (mime.includes('google-apps.document')) {
      return `https://docs.google.com/document/d/${driveId}/preview`
    }
    if (mime.includes('google-apps.spreadsheet')) {
      return `https://docs.google.com/spreadsheets/d/${driveId}/preview`
    }
    if (mime.includes('google-apps.presentation')) {
      return `https://docs.google.com/presentation/d/${driveId}/preview`
    }
    if (mime.includes('google-apps.drawing')) {
      return `https://docs.google.com/drawings/d/${driveId}/preview`
    }

    // PDF - use Google Drive viewer
    if (mime.includes('pdf')) {
      return `https://drive.google.com/file/d/${driveId}/preview`
    }

    // Images - use direct link or thumbnail
    if (mime.includes('image')) {
      return `https://drive.google.com/uc?id=${driveId}`
    }

    // Video
    if (mime.includes('video')) {
      return `https://drive.google.com/file/d/${driveId}/preview`
    }

    // Default - use Drive preview
    return `https://drive.google.com/file/d/${driveId}/preview`
  }

  // Check if file can be previewed
  const canPreview = () => {
    const mime = file.mime_type || ''
    return (
      mime.includes('google-apps') ||
      mime.includes('pdf') ||
      mime.includes('image') ||
      mime.includes('video')
    )
  }

  // Get file type icon
  const getFileIcon = () => {
    const mime = file.mime_type || ''
    if (mime.includes('image')) return <HiOutlinePhotograph className="w-5 h-5" />
    return <HiOutlineDocument className="w-5 h-5" />
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full h-full md:w-[95vw] md:h-[95vh] md:max-w-7xl bg-white md:rounded-xl overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-900 text-white">
          <div className="flex items-center gap-3 min-w-0">
            {getFileIcon()}
            <h3 className="font-medium truncate" title={file.name}>
              {file.name}
            </h3>
            {file.file_size && (
              <span className="text-xs text-gray-400 hidden sm:block">
                ({driveService.formatFileSize(file.file_size)})
              </span>
            )}
            {/* File counter */}
            {totalFiles > 1 && currentIndex !== undefined && (
              <span className="text-xs text-gray-400 hidden sm:block">
                ({currentIndex + 1}/{totalFiles})
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {/* Navigation buttons */}
            {(hasPrevious || hasNext) && (
              <>
                <button
                  onClick={onPrevious}
                  disabled={!hasPrevious}
                  className={clsx(
                    'p-2 rounded-lg transition-colors',
                    hasPrevious ? 'hover:bg-white/10' : 'opacity-30 cursor-not-allowed'
                  )}
                  title="File truoc (←)"
                >
                  <HiOutlineChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={onNext}
                  disabled={!hasNext}
                  className={clsx(
                    'p-2 rounded-lg transition-colors',
                    hasNext ? 'hover:bg-white/10' : 'opacity-30 cursor-not-allowed'
                  )}
                  title="File sau (→)"
                >
                  <HiOutlineChevronRight className="w-5 h-5" />
                </button>
                <div className="w-px h-5 bg-white/20 mx-1" />
              </>
            )}
            <button
              onClick={handleCopyLink}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Copy link (C)"
            >
              <HiOutlineLink className="w-5 h-5" />
            </button>
            <button
              onClick={handleOpenOriginal}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Mo file goc (O)"
            >
              <HiOutlineExternalLink className="w-5 h-5" />
            </button>
            <button
              onClick={handleDownload}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Tai ve (D)"
            >
              <HiOutlineDownload className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors ml-2"
              title="Dong (Esc)"
            >
              <HiOutlineX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-gray-100 relative">
          {canPreview() ? (
            <>
              {/* Loading indicator */}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">Dang tai...</p>
                  </div>
                </div>
              )}

              {/* Error state */}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center px-4">
                    <HiOutlineDocument className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 mb-4">Khong the xem truoc file nay</p>
                    <button
                      onClick={handleOpenOriginal}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Mo file goc
                    </button>
                  </div>
                </div>
              )}

              {/* Iframe */}
              <iframe
                src={getEmbedUrl()}
                className={clsx(
                  'w-full h-full border-0',
                  loading && 'invisible'
                )}
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false)
                  setError(true)
                }}
                allow="autoplay"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            </>
          ) : (
            // Cannot preview
            <div className="h-full flex items-center justify-center">
              <div className="text-center px-4">
                <HiOutlineDocument className="w-20 h-20 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-600 mb-2 font-medium">{file.name}</p>
                <p className="text-gray-400 text-sm mb-6">
                  Dinh dang file nay khong ho tro xem truoc
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={handleOpenOriginal}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                  >
                    <HiOutlineExternalLink className="w-4 h-4" />
                    Mo file goc
                  </button>
                  <button
                    onClick={handleDownload}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
                  >
                    <HiOutlineDownload className="w-4 h-4" />
                    Tai ve
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Mobile actions */}
        <div className="md:hidden flex items-center justify-around py-3 bg-white border-t border-gray-200">
          <button
            onClick={handleCopyLink}
            className="flex flex-col items-center gap-1 text-gray-600"
          >
            <HiOutlineLink className="w-5 h-5" />
            <span className="text-xs">Copy link</span>
          </button>
          <button
            onClick={handleOpenOriginal}
            className="flex flex-col items-center gap-1 text-gray-600"
          >
            <HiOutlineExternalLink className="w-5 h-5" />
            <span className="text-xs">Mo goc</span>
          </button>
          <button
            onClick={handleDownload}
            className="flex flex-col items-center gap-1 text-gray-600"
          >
            <HiOutlineDownload className="w-5 h-5" />
            <span className="text-xs">Tai ve</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default FilePreviewModal
